import { CloudUpload, Upload } from "lucide-react";
import { type DragEvent, useId, useRef, useState } from "react";

import type { DocumentCategory } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useUploadQueue } from "../../hooks/use-upload-queue.ts";
import {
	DOCUMENT_CATEGORIES,
	documentCategoryLabel,
	isDocumentCategory,
} from "../../utils/categories.ts";
import { DOCUMENT_ACCEPT, MAX_DOCUMENT_SIZE } from "../../utils/file-types.ts";
import { formatFileSize } from "../../utils/format-size.ts";
import { isUploadActive } from "../../utils/upload-queue.ts";
import { UploadQueueItem } from "../upload-queue-item";

export function UploadDialog({ constructionId }: { constructionId: string }) {
	const { queue, items } = useUploadQueue(constructionId);
	const inputRef = useRef<HTMLInputElement>(null);
	const ids = useId();
	const [open, setOpen] = useState(false);
	const [category, setCategory] = useState<DocumentCategory | undefined>();
	const [clientVisible, setClientVisible] = useState(false);
	const [dragging, setDragging] = useState(false);

	const readyCount = items.filter((item) => item.status === "ready").length;
	const activeCount = items.filter(
		(item) => isUploadActive(item) || item.status === "queued",
	).length;
	const finishedCount = items.filter(
		(item) => item.status === "done" || item.status === "rejected",
	).length;

	function handleOpenChange(next: boolean) {
		setOpen(next);
		if (!next) {
			queue.reset();
			setCategory(undefined);
			setClientVisible(false);
			setDragging(false);
		}
	}

	function addFiles(files: FileList | null) {
		if (!files || files.length === 0) return;
		queue.add(Array.from(files));
	}

	function handleDrop(event: DragEvent<HTMLElement>) {
		event.preventDefault();
		setDragging(false);
		addFiles(event.dataTransfer.files);
	}

	function handleDragOver(event: DragEvent<HTMLElement>) {
		event.preventDefault();
		event.dataTransfer.dropEffect = "copy";
		setDragging(true);
	}

	function send() {
		if (!category) return;
		queue.send({ category, clientVisible });
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button>
					<Upload /> Enviar documentos
				</Button>
			</DialogTrigger>
			<DialogContent
				className="document-upload"
				onOpenAutoFocus={(event) => event.preventDefault()}
				onInteractOutside={(event) => {
					if (activeCount > 0) event.preventDefault();
				}}
			>
				<DialogHeader>
					<DialogTitle>Enviar documentos</DialogTitle>
					<DialogDescription>
						PDF, imagens, DWG, DXF, Word ou Excel com até{" "}
						{formatFileSize(MAX_DOCUMENT_SIZE)} cada.
					</DialogDescription>
				</DialogHeader>

				<button
					type="button"
					className="document-upload__dropzone"
					data-dragging={dragging}
					onClick={() => inputRef.current?.click()}
					onDragEnter={handleDragOver}
					onDragOver={handleDragOver}
					onDragLeave={() => setDragging(false)}
					onDrop={handleDrop}
				>
					<CloudUpload />
					<span className="document-upload__dropzone-title">
						Arraste arquivos aqui ou clique para escolher
					</span>
					<span className="document-upload__dropzone-hint">
						Você pode selecionar vários arquivos de uma vez.
					</span>
				</button>
				<input
					ref={inputRef}
					type="file"
					multiple
					accept={DOCUMENT_ACCEPT}
					className="document-upload__input"
					aria-label="Escolher arquivos"
					data-testid="document-upload-input"
					onChange={(event) => {
						addFiles(event.target.files);
						event.target.value = "";
					}}
				/>

				<div className="document-upload__options">
					<div className="document-upload__field">
						<Label htmlFor={`${ids}-category`}>Categoria</Label>
						<Select
							value={category ?? ""}
							onValueChange={(next) =>
								setCategory(isDocumentCategory(next) ? next : undefined)
							}
						>
							<SelectTrigger
								id={`${ids}-category`}
								className="document-upload__category"
							>
								<SelectValue placeholder="Selecione a categoria" />
							</SelectTrigger>
							<SelectContent>
								{DOCUMENT_CATEGORIES.map((option) => (
									<SelectItem key={option} value={option}>
										{documentCategoryLabel(option)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="document-upload__switch">
						<Switch
							id={`${ids}-client-visible`}
							checked={clientVisible}
							onCheckedChange={setClientVisible}
						/>
						<Label htmlFor={`${ids}-client-visible`}>
							Visível para o cliente
						</Label>
					</div>
				</div>

				{items.length > 0 && (
					<ul className="document-upload__queue" aria-label="Arquivos">
						{items.map((item) => (
							<UploadQueueItem
								key={item.id}
								item={item}
								onRetry={(id) => queue.retry(id)}
								onRemove={(id) => queue.remove(id)}
							/>
						))}
					</ul>
				)}

				<DialogFooter className="document-upload__footer">
					{finishedCount > 0 && activeCount === 0 && (
						<Button
							type="button"
							variant="ghost"
							onClick={() => queue.clearFinished()}
						>
							Limpar concluídos
						</Button>
					)}
					<Button
						type="button"
						variant="outline"
						onClick={() => handleOpenChange(false)}
					>
						{activeCount > 0 ? "Cancelar envios" : "Fechar"}
					</Button>
					<Button
						type="button"
						onClick={send}
						disabled={!category || readyCount === 0}
					>
						<Upload />
						{readyCount > 1 ? `Enviar ${readyCount} arquivos` : "Enviar"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
